import {
  CButton,
  CCol,
  CInput,
  CLabel,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  getCatalogItemPrices,
  getPriceReference,
} from "../../api/catalog-items";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import { InvoiceItem } from "../../models/invoice";
import CatalogItem, {
  MEASURE_UNIT_UNIT,
  PriceReference,
} from "../../models/catalog-item";
import { PYG } from "../../currency/available-currencies";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { INVOICES_CREATE, INVOICES_UPDATE } from "../../auth/permissions";
import CatalogItemPriceHint from "../catalog-item/CatalogItemPriceHint";
import CatalogItemSelect from "../catalog-item/CatalogItemSelect";
import CatalogItemUpdateModal from "../catalog-item/CatalogItemUpdateModal";
import MeasureUnitSelect from "../measure-unit/MeasureUnitSelect";
import HasAnyPermissionComponent from "../permissions/HasAnyPermissionComponent";
import { successAlert } from "../utils/messages";
import CurrencyField from "../currencies/CurrencyField";
import { FieldErrors } from "../form/FieldErrors";
import {
  emptyValueOnUndefined,
  emptyValueOnUndefinedOrNull,
} from "../../utils/fields";
import {
  IVA_TYPE_10_PERCENT,
  IVA_TYPE_5_PERCENT,
  IVA_TYPE_CHOICES,
  IVA_TYPE_EXEMPT,
  IVA_TYPE_MIXED_10_PERCENT,
  IVA_TYPE_MIXED_5_PERCENT,
} from "../../models/iva";
import Client, { CLIENT_TYPE_GOVERNMENT } from "../../models/client";

const addTotalAmount = (
  item: InvoiceItem,
  unitAmount?: number,
  quantity?: number
): InvoiceItem => {
  if (unitAmount !== undefined && quantity !== undefined) {
    item.totalAmount = unitAmount * quantity;
  }

  return item;
};

const addAmountValues = (item: InvoiceItem) => {
  const newItem = { ...item };

  newItem.amount10Percent = undefined;
  newItem.amount5Percent = undefined;
  newItem.amountExempt = undefined;
  newItem.taxed10Percent = undefined;
  newItem.taxed5Percent = undefined;
  newItem.iva10Percent = undefined;
  newItem.iva5Percent = undefined;

  if (newItem.totalAmount !== undefined) {
    if (newItem.ivaType === IVA_TYPE_10_PERCENT) {
      newItem.amount10Percent = newItem.totalAmount;
      newItem.taxed10Percent = Math.round(newItem.totalAmount / (1 + 0.1));
      newItem.iva10Percent = Math.round(
        (newItem.totalAmount / (1 + 0.1)) * 0.1
      );
    }

    if (newItem.ivaType === IVA_TYPE_5_PERCENT) {
      newItem.amount5Percent = newItem.totalAmount;
      newItem.taxed5Percent = Math.round(newItem.totalAmount / (1 + 0.05));
      newItem.iva5Percent = Math.round(
        (newItem.totalAmount / (1 + 0.05)) * 0.05
      );
    }

    if (newItem.ivaType === IVA_TYPE_EXEMPT) {
      newItem.amountExempt = newItem.totalAmount;
    }
  }

  return newItem;
};

const addMissingValues = (item: InvoiceItem) => {
  const newItem = { ...item };
  // Unit, which is what the panel has always sent and the only one it offers.
  // An entry picked from the catalogue may carry another one, and then that one
  // is kept.
  newItem.measureUnit = item.measureUnit ?? MEASURE_UNIT_UNIT;

  return newItem;
};

/** How long to wait after the last keystroke before asking about the price. */
const PRICE_CHECK_DEBOUNCE_MS = 700;

interface InvoiceItemFormProps {
  item: InvoiceItem;
  client: Client | null;
  currency: string;
  onDelete: (_: InvoiceItem) => void;
  onChange: (_: InvoiceItem) => void;
  errors: Errors;
}

const InvoiceItemForm: React.FC<InvoiceItemFormProps> = ({
  item,
  client,
  currency,
  onDelete,
  onChange,
  errors,
}) => {
  const [editingItem, setEditingItem] = useState(item);
  const taxPayerId = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer.id,
  );
  const [catalogItem, setCatalogItem] = useState<CatalogItem | null>(null);
  const [prices, setPrices] = useState<number[]>([]);
  const [pricesForThisClient, setPricesForThisClient] = useState(false);
  const [priceReference, setPriceReference] = useState<
    PriceReference | undefined
  >(undefined);
  const priceColumnRef = useRef<HTMLDivElement | null>(null);
  const [showCatalogItemModal, setShowCatalogItemModal] = useState(false);
  const catalogSelectionRequest = useRef(0);

  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  /**
   * Fills the invoice line from the selected product or service. A suggested
   * price applies only in the invoice currency; the user can still change it.
   *
   * Clearing the selection leaves every field as it is. Whatever was typed
   * afterwards is the person's, not the catalogue's.
   */
  const onCatalogItemChange = useCallback(
    async (entry: CatalogItem | null) => {
      const request = ++catalogSelectionRequest.current;
      setCatalogItem(entry);
      setPrices([]);
      setPricesForThisClient(false);

      if (entry === null) {
        return;
      }

      let newItem = { ...editingItem };
      newItem.description = entry.description ?? newItem.description;

      if (entry.code) {
        newItem.code = entry.code;
      }

      if (entry.ivaType) {
        newItem.ivaType = entry.ivaType;
        newItem.ivaBase =
          entry.ivaType === IVA_TYPE_10_PERCENT ||
          entry.ivaType === IVA_TYPE_5_PERCENT
            ? 100
            : entry.ivaType === IVA_TYPE_EXEMPT
              ? 0
              : undefined;
      }

      newItem.measureUnit = entry.measureUnit ?? MEASURE_UNIT_UNIT;
      // One unless a quantity was already typed: it is the usual case, and a
      // quantity the person chose is theirs.
      newItem.quantity = newItem.quantity ?? 1;
      const unitPrice =
        entry.unitPrice != null &&
        (entry.priceCurrency ?? PYG) === currency
          ? Number(entry.unitPrice)
          : undefined;
      if (unitPrice !== undefined) {
        newItem.unitAmount = unitPrice;
        newItem.totalAmount = unitPrice * newItem.quantity;
      }
      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
      setPriceReference(undefined);

      if (unitPrice === undefined) {
        // The price field belongs to react-currency-input, which takes no ref.
        priceColumnRef.current?.querySelector("input")?.focus();
      }

      if (entry.id === undefined || taxPayerId === undefined) {
        return;
      }

      const status = await getCatalogItemPrices(
        entry.id,
        taxPayerId,
        client?.ruc,
      );

      if (
        request === catalogSelectionRequest.current &&
        status.status === SUCCESS &&
        status.data !== undefined
      ) {
        setPrices(status.data.prices);
        setPricesForThisClient(status.data.forThisClient);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem, client, taxPayerId, currency],
  );

  const onCatalogItemCreated = (created?: CatalogItem) => {
    setShowCatalogItemModal(false);
    if (created !== undefined) {
      onCatalogItemChange(created);
      successAlert("Producto o servicio guardado.");
    }
  };

  const onQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };
      if (!isNaN(parseFloat(e.target.value))) {
        newItem.quantity = parseFloat(e.target.value);
        newItem = addTotalAmount(
          newItem,
          newItem.unitAmount,
          parseFloat(e.target.value)
        );
      } else {
        newItem.quantity = undefined;
        newItem = addTotalAmount(newItem, newItem.unitAmount, undefined);
      }

      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  /**
   * Editing the code or the description by hand makes the line something else:
   * the entry picked before, and the prices charged for it, no longer apply.
   */
  const clearCatalogItem = () => {
    if (catalogItem !== null) {
      setCatalogItem(null);
      setPrices([]);
      setPricesForThisClient(false);
    }
  };

  const onCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.code = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
      clearCatalogItem();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem, catalogItem]
  );

  const onDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.description = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
      clearCatalogItem();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem, catalogItem]
  );

  const onDncpLevelCodeGeneralChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.dncpLevelCodeGeneral = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDncpLevelCodeSpecificChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newItem = { ...editingItem };
      newItem.dncpLevelCodeSpecific = e.target.value;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onUnitAmountChange = useCallback(
    (newUnitAmount?: number) => {
      let newItem = { ...editingItem };
      newItem.unitAmount = newUnitAmount;
      newItem = addTotalAmount(newItem, newUnitAmount, newItem.quantity);
      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onDiscountChange = useCallback(
    (newDiscount?: number) => {
      let newItem = { ...editingItem };
      newItem.discount = newDiscount;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onIvaTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };
      newItem.ivaType = e.target.value;

      if (
        newItem.ivaType === IVA_TYPE_10_PERCENT ||
        newItem.ivaType === IVA_TYPE_5_PERCENT
      ) {
        newItem.ivaBase = 100;
      } else if (newItem.ivaType === IVA_TYPE_EXEMPT) {
        newItem.ivaBase = 0;
      } else {
        newItem.ivaBase = undefined;
      }

      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onMeasureUnitChange = useCallback(
    (code: number) => {
      const newItem = { ...editingItem };
      newItem.measureUnit = code;
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  const onIvaBaseChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newItem = { ...editingItem };

      if (!isNaN(parseFloat(e.target.value))) {
        newItem.ivaBase = parseFloat(e.target.value);
      } else {
        newItem.ivaBase = undefined;
      }

      newItem = addAmountValues(newItem);
      newItem = addMissingValues(newItem);

      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem]
  );

  useEffect(() => {
    setEditingItem(item);
  }, [item]);

  /**
   * Asks whether the price that was typed is an order of magnitude off.
   *
   * Debounced rather than fired on blur because the price field is shared with
   * five other document forms and takes no blur handler; waiting out the
   * keystrokes gets the same answer without touching it.
   *
   * Independent of the catalogue: it matches on this line's own description or
   * code, so it also warns the tax payer who never built one.
   */
  useEffect(() => {
    const description = (editingItem.description ?? "").trim();
    const code = (editingItem.code ?? "").trim();
    const unitAmount = editingItem.unitAmount;

    if (
      taxPayerId === undefined ||
      unitAmount === undefined ||
      unitAmount <= 0 ||
      (description === "" && code === "")
    ) {
      setPriceReference(undefined);
      return;
    }

    const timer = setTimeout(async () => {
      const status = await getPriceReference(
        taxPayerId,
        description,
        code === "" ? undefined : code,
        unitAmount,
      );

      if (status.status === SUCCESS && status.data !== undefined) {
        setPriceReference(status.data);
      }
    }, PRICE_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    taxPayerId,
    editingItem.description,
    editingItem.code,
    editingItem.unitAmount,
  ]);

  const itemLabelStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "var(--theme-muted)",
    marginBottom: "3px",
    display: "block",
  };

  /**
   * The same label, pinned to a single line.
   *
   * Only the row of short fields uses it. That row aligns its columns by the
   * top so that what grows underneath one field — the price hint, a validation
   * error, the currency input's own invalid feedback — stops dragging its
   * neighbours down with it. Aligning by the top is what makes a label that
   * wraps on a narrow phone push its own field below the others, so no label
   * there is allowed to wrap.
   */
  const shortFieldLabelStyle: React.CSSProperties = {
    ...itemLabelStyle,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <div
      style={{
        position: "relative",
        border: "1px solid var(--theme-border)",
        borderLeft: "4px solid #006657",
        borderRadius: "6px",
        marginBottom: "10px",
        padding: "12px 14px 10px",
        background: "var(--theme-surface)",
      }}
    >
      {/* Botón eliminar: siempre arriba a la derecha */}
      <div style={{ position: "absolute", top: "10px", right: "12px" }}>
        <CButton
          color="danger"
          variant="outline"
          size="sm"
          onClick={onDeleteClick}
        >
          <i className="fa fa-trash"></i>
        </CButton>
      </div>

      {/* Buscador del catálogo: completa código, IVA y unidad */}
      <CRow className="mb-2" style={{ paddingRight: "46px" }}>
        <CCol xs={12} md={6}>
          <CLabel style={itemLabelStyle}>Producto o servicio</CLabel>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <CatalogItemSelect
                value={catalogItem}
                onChange={onCatalogItemChange}
              />
            </div>
            <HasAnyPermissionComponent
              permissionNames={[INVOICES_CREATE, INVOICES_UPDATE]}
            >
              <CButton
                type="button"
                color="primary"
                variant="outline"
                className="ml-2"
                title="Crear producto o servicio"
                onClick={() => setShowCatalogItemModal(true)}
              >
                <i className="fa fa-plus"></i>
              </CButton>
            </HasAnyPermissionComponent>
          </div>
        </CCol>
      </CRow>
      <CatalogItemUpdateModal
        show={showCatalogItemModal}
        currency={currency}
        onCancel={() => setShowCatalogItemModal(false)}
        onSuccess={onCatalogItemCreated}
      />

      {/* Fila 1: Cantidad | Código | Precio Unitario | Descuento */}
      {/*
        Alineada por arriba, no por abajo: debajo del precio pueden aparecer el
        hint de precios anteriores y los errores del campo, y con las columnas
        pegadas por abajo eso empujaba al resto de la fila y descuadraba el ítem.
      */}
      <CRow className="align-items-start mb-2" style={{ paddingRight: "46px" }}>
        <CCol xs={4} md={1}>
          <CLabel style={shortFieldLabelStyle}>
            <span className="text-danger">*</span> Cant.
          </CLabel>
          <CInput
            type="number"
            step="0.001"
            value={emptyValueOnUndefined(editingItem.quantity)}
            onChange={onQuantityChange}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("quantity", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={8} md={2}>
          <CLabel style={shortFieldLabelStyle}>
            <span className="text-danger">*</span> Código
          </CLabel>
          <CInput
            type="text"
            value={emptyValueOnUndefined(editingItem.code)}
            onChange={onCodeChange}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("code", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={6} md={3}>
          <CLabel style={shortFieldLabelStyle}>
            <span className="text-danger">*</span> Precio
            <span className="d-none d-md-inline"> Unitario</span>
          </CLabel>
          <div ref={priceColumnRef}>
            <CurrencyField
              currency={currency}
              placeholder="Monto"
              onChange={onUnitAmountChange}
              value={editingItem.unitAmount}
              limit={1000000000000}
            />
          </div>
          <FieldErrors
            errors={getFieldErrors("unitAmount", errors) as string[]}
          ></FieldErrors>
          <CatalogItemPriceHint
            currency={currency}
            prices={prices}
            pricesForThisClient={pricesForThisClient}
            reference={priceReference}
          />
        </CCol>
        <CCol xs={6} md={3}>
          <CLabel style={shortFieldLabelStyle}>Descuento</CLabel>
          <CurrencyField
            currency={currency}
            placeholder="Monto"
            onChange={onDiscountChange}
            value={editingItem.discount}
            limit={1000000000000}
          />
          <FieldErrors
            errors={getFieldErrors("discount", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* Fila 2: Detalle */}
      <CRow className="mb-2">
        <CCol>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Detalle
          </CLabel>
          <CTextarea
            placeholder="Descripción del ítem"
            value={emptyValueOnUndefined(editingItem.description)}
            onChange={onDescriptionChange}
            style={{ resize: "vertical", minHeight: "60px" }}
          ></CTextarea>
          <FieldErrors
            errors={getFieldErrors("description", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* Fila 3: Tipo IVA | % IVA */}
      <CRow className="align-items-end">
        <CCol xs={8} md={3}>
          <CLabel style={itemLabelStyle}>
            <span className="text-danger">*</span> Tipo IVA
          </CLabel>
          <CSelect
            type="text"
            value={
              editingItem.ivaType ? editingItem.ivaType : IVA_TYPE_10_PERCENT
            }
            onChange={onIvaTypeChange}
          >
            <option value={""} disabled>
              -----
            </option>
            {Array.from(IVA_TYPE_CHOICES.entries()).map((entry) => {
              return (
                <option key={entry[0]} value={entry[0]}>
                  {entry[1]}
                </option>
              );
            })}
          </CSelect>
          <FieldErrors
            errors={getFieldErrors("ivaType", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={4} md={2}>
          <CLabel style={itemLabelStyle}>% IVA</CLabel>
          <CInput
            type="number"
            onChange={onIvaBaseChange}
            placeholder="Porcentaje"
            step={0.01}
            disabled={
              editingItem.ivaType !== IVA_TYPE_MIXED_10_PERCENT &&
              editingItem.ivaType !== IVA_TYPE_MIXED_5_PERCENT
            }
            value={emptyValueOnUndefined(editingItem.ivaBase)}
          ></CInput>
          <FieldErrors
            errors={getFieldErrors("ivaBase", errors) as string[]}
          ></FieldErrors>
        </CCol>
        <CCol xs={12} md={4}>
          <CLabel style={itemLabelStyle}>Unidad de medida</CLabel>
          <MeasureUnitSelect
            value={editingItem.measureUnit ?? MEASURE_UNIT_UNIT}
            onChange={onMeasureUnitChange}
          />
          <FieldErrors
            errors={getFieldErrors("measureUnit", errors) as string[]}
          ></FieldErrors>
        </CCol>
      </CRow>

      {/* DNCP (solo para clientes gubernamentales) */}
      {client !== null && client.type === CLIENT_TYPE_GOVERNMENT && (
        <>
          <hr style={{ margin: "10px 0", borderColor: "#f9b115", borderWidth: "2px" }} />
          <CRow className="align-items-end">
            <CCol xs={6} md={3}>
              <CLabel style={itemLabelStyle}>
                <span className="text-danger">*</span> Nivel DNCP General
              </CLabel>
              <CInput
                type="text"
                placeholder="Nivel general"
                onChange={onDncpLevelCodeGeneralChange}
                value={emptyValueOnUndefinedOrNull(
                  editingItem.dncpLevelCodeGeneral
                )}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("dncpLevelCodeGeneral", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
            <CCol xs={6} md={3}>
              <CLabel style={itemLabelStyle}>
                <span className="text-danger">*</span> Nivel DNCP Específico
              </CLabel>
              <CInput
                type="text"
                placeholder="Nivel específico"
                onChange={onDncpLevelCodeSpecificChange}
                value={emptyValueOnUndefinedOrNull(
                  editingItem.dncpLevelCodeSpecific
                )}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("dncpLevelCodeSpecific", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </>
      )}
    </div>
  );
};

const propsAreEqual = (
  prevItemProps: InvoiceItemFormProps,
  nextItemProps: InvoiceItemFormProps
): boolean => {
  return (
    prevItemProps.item.id === nextItemProps.item.id &&
    prevItemProps.item.code === nextItemProps.item.code &&
    prevItemProps.item.obs === nextItemProps.item.obs &&
    prevItemProps.item.dncpLevelCodeGeneral ===
      nextItemProps.item.dncpLevelCodeGeneral &&
    prevItemProps.item.dncpLevelCodeSpecific ===
      nextItemProps.item.dncpLevelCodeSpecific &&
    prevItemProps.item.measureUnit === nextItemProps.item.measureUnit &&
    prevItemProps.item.unitAmount === nextItemProps.item.unitAmount &&
    prevItemProps.item.description === nextItemProps.item.description &&
    prevItemProps.item.quantity === nextItemProps.item.quantity &&
    prevItemProps.item.totalAmount === nextItemProps.item.totalAmount &&
    prevItemProps.item.amount10Percent === nextItemProps.item.amount10Percent &&
    prevItemProps.item.amount5Percent === nextItemProps.item.amount5Percent &&
    prevItemProps.item.amountExempt === nextItemProps.item.amountExempt &&
    prevItemProps.item.ivaType === nextItemProps.item.ivaType &&
    prevItemProps.item.ivaBase === nextItemProps.item.ivaBase &&
    prevItemProps.item.iva10Percent === nextItemProps.item.iva10Percent &&
    prevItemProps.item.iva5Percent === nextItemProps.item.iva5Percent &&
    prevItemProps.client?.id === nextItemProps.client?.id &&
    errorsAreSame(prevItemProps.errors, nextItemProps.errors)
  );
};

export default React.memo(InvoiceItemForm, propsAreEqual);
